import type { CallappOptions } from 'callapp-lib/dist/type/types';
import { useCallback, useEffect, useState } from 'react';

import {
  AndroidApkDownloadLink,
  AppStoreLink,
  GooglePlayLink,
  isAppleMobile
} from '@/utils/call-app';
import { isProduct } from '@/utils/host';

import { useRegion } from './useAppEnv';

interface IProps {
  failRedirect?: string; // 自定义兜底（不传则按平台/region 选择商店）
  failCallback?: () => void; // 唤起失败回调（可做埋点）
}

const UL_HOST = 'go.second.me';
const UL_HOST_CN = 'go.second-me.cn';
const SCHEME = isProduct() ? 'mebot' : 'mebottest';
const ANDROID_PACKAGE = isProduct() ? 'com.mindverse.mebot' : 'com.mindverse.mindostest';
const ANDROID_INTENT_HOST = 'open';

type OpenFn = (path?: string, param?: Record<string, any>) => void;

const useOpenApp = (
  props?: IProps
): {
  /** 仅 scheme + intent（不走 UL） */
  openApp: OpenFn | undefined;
  /** 走 UL（你显式调用它时才走）；ai-home 会映射到 /?… */
  openULApp: OpenFn | undefined;
} => {
  const { failCallback, failRedirect } = props || {};
  const { region } = useRegion();

  const [libUL, setLibUL] = useState<any>(); // 专用于 UL 的实例
  const [libNoUL, setLibNoUL] = useState<any>(); // 仅 scheme+intent 的实例

  useEffect(() => {
    let mounted = true;

    import('callapp-lib').then((m) => {
      if (!mounted) return;

      const CallApp = m.default;

      // 平台自适应的 fallback
      const fallbackByEnv =
        failRedirect ??
        (isAppleMobile()
          ? AppStoreLink
          : region === 'cn'
            ? AndroidApkDownloadLink
            : GooglePlayLink);

      // A) 仅 scheme+intent（不启用 universal）
      const optsNoUL: CallappOptions = {
        scheme: { protocol: SCHEME, host: isAppleMobile() ? undefined : ANDROID_INTENT_HOST },
        intent: { scheme: SCHEME, package: ANDROID_PACKAGE },
        appstore: AppStoreLink,
        fallback: fallbackByEnv,
        timeout: 3000
      };

      // B) 启用 universal（你显式调用 openULApp 时才会用到）
      const optsUL: CallappOptions = {
        universal: { host: region === 'cn' ? UL_HOST_CN : UL_HOST }, // 不写 pathKey；open({ path:'' }) => https://go.second.me/?...
        scheme: { protocol: SCHEME, host: isAppleMobile() ? undefined : ANDROID_INTENT_HOST },
        intent: { scheme: SCHEME, package: ANDROID_PACKAGE },
        appstore: AppStoreLink,
        fallback: fallbackByEnv,
        timeout: 3000
      };

      setLibNoUL(new CallApp(optsNoUL));
      setLibUL(new CallApp(optsUL));
    });

    return () => {
      mounted = false;
    };
  }, [failRedirect, region]);

  /** 仅 scheme+intent（不走 UL） */
  const openApp = useCallback<OpenFn>(
    (path = '', param = {}) => {
      libNoUL?.open({ path, param, callback: failCallback });
    },
    [libNoUL, failCallback]
  );

  /**
   * 走 UL（你显式调用它时才走）
   * 约定：如果 path === 'ai-home'，你的 UL 需要根路径形式 `https://go.second.me/?route=xxx`
   * 我会把 path 置空，让 callapp-lib 生成根路径，然后把 param 拼到 query。
   */
  const openULApp = useCallback<OpenFn>(
    (path = '', param = {}) => {
      // ai-home → 根路径 /
      const pathForUL = path === 'ai-home' ? '' : path;

      libUL?.open({ path: pathForUL, param, callback: failCallback });
    },
    [libUL, failCallback]
  );

  return { openApp, openULApp };
};

export default useOpenApp;
